/**
 * @module lib/commercial/ad-sequencing
 */
import { getUserHashId, getSophi3Id } from "lib/identity/api";
import tgamConfig from "config/tgam";
import { setProp, getProp } from "utils/utils";
import {
  init as campaignId,
  updateCampaignAdState,
  updateCampaignNextStoryState,
  getCampaignAdState,
  getCampaignNextStoryState
} from "./campaigns";
import { mark, measure } from "lib/performance/user-timing";
import crow from "utils/crow";
import isNil from "lodash/isNil";
import logger from "utils/logger";
import isEmpty from "lodash/isEmpty";
import { getPresetKey } from "lib/commercial/dfp-ads/adslot-size-mapping";
import isEqual from "lodash/isEqual";
import find from "lodash/find";
import { isLocalStorageTypeSupported } from "lib/store";
import adSequencingReady from "lib/commercial/ad-sequencing/ready";

const API_TIMEOUT = getProp(tgamConfig, "adSequencing.timeout", 300);
const VISITOR_ID = (() => {
  const id = getSophi3Id();
  return !id ? "n" : id;
})();

const HASH_ID = getUserHashId();
const LOG = "[AD-SEQUENCING]";

const apis = {
  traffic: "https://xj5oetyjhd.execute-api.us-east-1.amazonaws.com/prod/tcop",
  creative: "https://xj5oetyjhd.execute-api.us-east-1.amazonaws.com/prod/ads"
};

function _debugger() {
  if (/debugger=true/ig.test(window.location.search)) {
    // eslint-disable-next-line no-debugger
    debugger;
  }
}

function shouldRunOnSection(path) {
  const adSeqSections = getProp(tgamConfig, "adSequencing.sections", "");
  if (adSeqSections === "*") {
    return true;
  }
  return !!(find(adSeqSections.split(","), k => path.indexOf(k) !== -1));
}

function doNotRun() {
  const lsUnavailable = !isLocalStorageTypeSupported("localStorage");
  const pbAdmin = getProp(tgamConfig, "env.isPbAdmin", false);
  const appView = getProp(tgamConfig, "env.inAppView", false);
  const advContent = getProp(tgamConfig, "meta.advContent", false);
  const isSectionPage = getProp(tgamConfig, "meta.isSectionPage", false);
  const isStaticPage = getProp(tgamConfig, "meta.isStatic", false);
  const isOrigin = /arcpublishing\.com/im.test(window.location.href);
  if (pbAdmin || isOrigin || appView || advContent || lsUnavailable || isStaticPage) {
    if (lsUnavailable) {
      const msg = "adSequencing will not run: localStorage is not supported";
      logger.info(`${LOG}`, msg);
      crow.message(msg);
    }
    return true;
  }
  if (isSectionPage && shouldRunOnSection(getProp(tgamConfig, "meta.urlRelative", null)) === false) {
    const msg = `adSequencing will not run on this section page. Applicable section fronts are: ${getProp(tgamConfig, "adSequencing.sections")}`;
    logger.info(`${LOG}`, msg);
    return true;
  }
  return false;
}

/**
 * A wrapper around XMLHttpRequest. Used instead of reqwest so as to avoid sending a pre-flight
 * OPTIONS request to the uri.
 * @param {String} uri
 * @param {String} method
 * @returns {Promise<Object>}
 */
function ajax(uri, method = "get") {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = () => {
      let data;
      try {
        data = JSON.parse(xhr.responseText);
        if (xhr.status === 200) {
          resolve(data);
        } else {
          reject(data);
        }
      } catch (e) {
        reject();
      }
    };
    xhr.open(method, uri, true);
    xhr.timeout = API_TIMEOUT;
    xhr.ontimeout = () => {
      reject();
    };
    xhr.send();
  });
}

/**
 * Resolve the deferred promise. All instances .then'ed before and after will execute.
 */
export function adSequencingReadyResolve() {
  // ee.trigger("adSequencing:ready"); - Deprecated. Use below Deferred Promise.
  adSequencingReady.resolve();
}

/**
 * Sets window.tgam.adSequencing.nextStoryDisplayed that is needed to determine if we should run nativo and update recommendations for article pages
 * @param {Boolean} shouldSeeCampaign
 */
function setNextStoryState(shouldSeeCampaign = false) {
  setProp(tgamConfig, "adSequencing.nextStoryDisplayed", shouldSeeCampaign);
}

/**
 * Makes an ajax request to the Traffic Cop API
 * @param {String|Number} id - the active campaign id
 */
function pingTrafficCop(id) {
  policeUser(id)
    .then(resp => {
      logger.info(`${LOG}`, `policeUser returned with: ${JSON.stringify(resp)}`);
      updateCampaignAdState(resp.brand_ads ? resp.brand_ads : false);
      updateCampaignNextStoryState(resp.ads_content ? resp.ads_content : false);
    })
    .catch(err => {
      logger.error(`${LOG}`, "policeUser failed: ", err);
      crow.error(new Error("policeUser failed"), {
        feature: "ad sequencing"
      });
    });
}

/**
 * Calls the Traffic Cop API
 * @param {String|Number} id - the campaign id
 * @returns {Promise<Object>}
 */
function policeUser(id) {
  return ajax(`${apis.traffic}?sophi_id=${VISITOR_ID}&hash_id=${isNil(HASH_ID) ? "" : HASH_ID}&campaign_id=${id}`);
}

/**
 * Calls the Creative API
 * @param {String|Number} id - the campaign id
 * @returns {Promise<Object>}
 */
function getCreativeForCampaign(id) {
  return ajax(`${apis.creative}?sophi_id=${VISITOR_ID}&hash_id=${isNil(HASH_ID) ? "" : HASH_ID}&campaign_id=${id}`);
}

/**
 * @method normalizeCreative
 * @description Process the creative response for easier comparision to the sizemapping.
 * Sort the creative ad creative items by their area, largest to smallest.
 * @param {Object} data - to be normalized
 * @returns {Object}
 */
export function normalizeCreative(data = {}) {
  let ret = {};
  Object.keys(data).forEach(slot => {
    ret[slot] = [];
    // map the ad creative items
    Object.keys(data[slot]).forEach(size => {
      const dimensions = size.split("x").map(Number);
      ret[slot].push({
        size: size, // "728x90"
        dim: dimensions, // [728, 90]
        area: dimensions[0] * dimensions[1], // 65520
        cid: data[slot][size] // creative id, e.g. "138251111433"
      });
    });
    // sort the creative items by advert area
    ret[slot].sort((first, second) => {
      if (first.area > second.area) {
        return 1;
      }
      if (first.area < second.area) {
        return -1;
      }
      return 0; // values are equal
    }).reverse(); // largest to smallest
  });
  return ret;
}

/**
 * @method getCreativeIdForAdvert
 * @description Get the creative ID related to a specific advertisement
 * @param {String} id - of the ad unit
 * @param {Array} sizemap - associated with the ad unit
 * @param {Number} bp - current viewport tier breakpoint integer
 * @param {Object} creatives - to search through for a match
 * @returns {String|null}
 */
export function getCreativeIdForAdvert(id, sizemap, bp, creatives = getProp(tgamConfig, "adSequencing.creative")) {
  let ret = null;
  // if not creative size items were returned from the API
  if (!creatives) {
    return ret;
  }
  // find the creative size items for this ad unit
  const slotMap = creatives[getPresetKey(id)];
  if (!slotMap) {
    return ret;
  }
  // for each size item within the creative slot
  Object.keys(slotMap).forEach(size => {
    // for each viewport tier within the ad's sizemap
    sizemap.forEach(tier => {
      // if the tier's breakpoint is not the active one
      if (tier[0][0] !== bp) {
        return;
      }
      // for each size within the tier
      tier[1].forEach(s => {
        // find the first match of creative item size and tier item size
        if (!ret && isEqual(slotMap[size].dim, s)) {
          ret = slotMap[size].cid; // creative id found
        }
      });
    });
  });
  return ret;
}

/**
 * If there is no active campaign, do nothing!
 * Otherwise:
 *  - determine if the current user should be part of this campaign
 *  - if so, get the creative ids for this campaign, then update
 *    the adops page targets "cid" property with this value
 *  - if not, save state to local storage
 *  - 100% of the time, call the Traffic Cop API after page load to update
 * the user state (whether they should be part of this campaign)
 */
export default function init() {
  mark("adsequencing_started");
  logger.info(`${LOG} init`);
  const activeCampaignId = campaignId();
  if (doNotRun() || !activeCampaignId) {
    logger.info(`${LOG}`, `doNotRun: ${doNotRun()}, activeCampaignId: !${activeCampaignId}`);
    adSequencingReadyResolve();
    mark("adsequencing_done");
    measure("adsequencing");
    return;
  }
  const adShouldBeSeenByThisUser = getCampaignAdState() || /adseqad=true/.test(window.location.search);
  const nextStoryShouldBeSeenByThisUser = getCampaignNextStoryState() || /adseqnext=true/.test(window.location.search);
  logger.info(`${LOG} adShouldBeSeenByThisUser : ${adShouldBeSeenByThisUser }`);
  logger.info(`${LOG} nextStoryShouldBeSeenByThisUser: ${ nextStoryShouldBeSeenByThisUser}`);
  if (!adShouldBeSeenByThisUser) {
    setNextStoryState(nextStoryShouldBeSeenByThisUser);
    adSequencingReadyResolve();
    mark("adsequencing_done");
    measure("adsequencing");
  } else {
    setNextStoryState(nextStoryShouldBeSeenByThisUser);
    getCreativeForCampaign(activeCampaignId)
      .then(data => {
        _debugger();
        window.tgam.adSequencing.creative = isEmpty(data) ? null : normalizeCreative(data);
        logger.info(`${LOG} getCreativeForCampaign:`, window.tgam.adSequencing.creative);
      })
      .catch(() => {
        _debugger();
        window.tgam.adSequencing.creative = null;
        logger.error(`${LOG} getCreativeForCampaign failed`);
        crow.error(new Error("getCreativeForCampaign failed"), {
          feature: "ad sequencing"
        });
      })
      .then(() => {
        adSequencingReadyResolve();
        mark("adsequencing_done");
        measure("adsequencing");
      });
  }
  // Since the traffic cop may change the user state from true to false or vice versa
  // we should ping on every page view.
  //
  // We polyfill requestIdleCallback in global-polyfill.js, which is included in head-inline-js-config.jsp,
  // but Safari seems to be parsing preloaded scripts (ie <link rel="preload" src="X.js">) _before_ the code in
  // head-inline-js-config.jsp is parsed and it throws a "requestIdleCallback undefined" error.
  // Therefore, check first that requestIdleCallback is a widnow property and if not handle things differently
  // ¯\_(ツ)_/¯
  if (getProp(window, "requestIdleCallback")) {
    window.requestIdleCallback(() => {
      pingTrafficCop(activeCampaignId);
    });
  } else {
    setTimeout(() => {
      pingTrafficCop(activeCampaignId);
    }, 2000);
  }
};
